import { getFirestore, doc, updateDoc, deleteDoc, serverTimestamp, writeBatch, collection } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { MediaService } from "../../services/media.service.js";
import { WebtoonHub } from "./WebtoonHub.js";
import { WebtoonEpisodeEditor } from "./WebtoonEpisodeEditor.js";
import { EpisodeAdapter } from "../../services/episode-adapter.service.js"; // [REFACTOR] Use Adapter

const db = getFirestore(app);

/**
 * [Webtoon Editor Controller]
 * Orchestrates Hub and EpisodeEditor.
 * Handles Database Operations and State Management.
 */
export class WebtoonEditor {
    constructor(container, id, data, subEpisodes = []) {
        this.container = container;
        this.seriesId = id;
        this.data = data;

        // [REFACTOR] Use Adapter for Resume/Legacy Checks
        // This ensures 'status' is defaulted to 'published' for legacy data.
        this.episodes = EpisodeAdapter.adapt(data, subEpisodes);

        this.currentEditorIndex = -1;

        // Internal State
        this.files = [];
    }

    render() {
        // [FIX] Lifecycle Management: Cleanup previous instance to kill zombie AutoSaves
        if (this.currentEditorInstance && typeof this.currentEditorInstance.destroy === 'function') {
            this.currentEditorInstance.destroy();
        }
        this.currentEditorInstance = null; // Clear ref

        this.container.innerHTML = '';
        if (this.currentEditorIndex === -1) {
            const hub = new WebtoonHub(this.container, this);
            hub.render();
            // Hub doesn't need lifecycle tracking yet
        } else {
            const editor = new WebtoonEpisodeEditor(this.container, this, this.currentEditorIndex);
            this.currentEditorInstance = editor; // [Track]
            editor.render();
        }
    }

    // --- Actions called by Views ---

    triggerNewEpisode() {
        this.files = []; // Reset files for new ep
    }

    async saveMeta() {
        const hubHtml = this.container;
        if (!hubHtml) return;

        const titleInput = hubHtml.querySelector('#hub-title');
        const coverInput = hubHtml.querySelector('#hub-cover');
        const descInput = hubHtml.querySelector('#hub-desc');
        const tagsInput = hubHtml.querySelector('#hub-tags');
        const isbnInput = hubHtml.querySelector('#hub-isbn');
        const uciInput = hubHtml.querySelector('#hub-uci');

        if (!titleInput) return;

        const title = titleInput.value.trim();
        const coverImage = coverInput.value.trim();
        const description = descInput.value;
        const tags = tagsInput.value.split(',').map(t => t.trim()).filter(t => t);

        // Admin Fields
        const updateData = {
            title, coverImage, description, tags, updatedAt: serverTimestamp()
        };

        if (isbnInput) updateData.isbn = isbnInput.value.trim();
        if (uciInput) updateData.uci = uciInput.value.trim();

        // [FIX] Ownership Protection: Strict Admin Check
        const currentUser = window.user || {};
        const isAdmin = currentUser.role === 'admin' || currentUser.isSuperAdmin;

        if (isAdmin) {
            console.log("[Webtoon] Admin Save: Protecting Author ID");
            delete updateData.authorId;
        }

        try {
            await updateDoc(doc(db, "series", this.seriesId), updateData);
            if (window.Modal) window.Modal.toast("Series Info Saved");
        } catch (e) {
            console.error(e);
            window.Modal.alert("Error saving series info: " + e.message);
        }
    }

    async saveEpisode(index, data, silent = false) {
        if (!silent) {
            // Show Spinner if needed
        }

        try {
            // [FIX] Generate/Retrieve Episode ID EARLY for Upload Path Consistency
            // User 'data.id' if provided (from Editor), otherwise fallback to existing state or generate new.
            let epId = data.id || this.episodes[index]?.id;
            const episodesRef = collection(db, "series", this.seriesId, "episodes");
            let epRef;

            if (epId) {
                epRef = doc(episodesRef, epId);
            } else {
                epRef = doc(episodesRef);
                epId = epRef.id;
            }

            // Ensure local state has ID
            if (!this.episodes[index]) {
                this.episodes[index] = {};
            }
            this.episodes[index].id = epId;

            // 1. Upload Pending Files (Now using GUARANTEED UUID Path)
            const newLinks = [...this.files];
            const pending = newLinks.map((item, i) => ({ item, i })).filter(({ item }) => item.isLocal);

            if (pending.length > 0) {
                if (!silent) console.log("Uploading pending files...");
                window.Modal.toast(window.locale.t('editor.uploading_images_toast'), window.locale.t('editor.saving')); // OR 'Save' -> verify key


                for (const { item, i } of pending) {
                    // [FIX] Compress Image (WebP 0.8)
                    const compressed = await MediaService.compressImage(item.file, 0.8);

                    // [FIX] Restore Standard Path: series/{id}/ep_{index}/...
                    // [CRITICAL CHANGE] Switching to UUID-based path to prevent "Index Reuse" collisions (Ghost Data)
                    // Old: series/{id}/ep_{index}/... -> caused collision if index reused after delete.
                    // New: series/{id}/episodes/{uuid}/... -> Unique per document.
                    const path = `series/${this.seriesId}/episodes/${epId}/${Date.now()}_${i}.webp`;

                    console.log("[WebtoonEditor] Uploading to:", path);
                    const url = await MediaService.uploadImage(compressed, path);
                    item.url = url;
                    item.isLocal = false;
                    item.status = 'done';
                }
                this.files = newLinks;
            }

            // 2. Make Links (Priority: Editor Data > Controller State)
            // [FIX] Use links passed from EpisodeEditor if available.
            const finalLinks = (data.links && data.links.length > 0) ? data.links :
                (data.imageUrls && data.imageUrls.length > 0) ? data.imageUrls :
                    this.files.map(f => f.url).filter(u => u);

            // [FIX] BGM Mapping
            // EpisodeEditor passes 'bgmUrl', DB expects 'bgm' or 'bgmUrl'.
            // Ensure data.bgm is populated if bgmUrl is present
            if (data.bgmUrl && !data.bgm) {
                data.bgm = data.bgmUrl;
            }

            // 3. Update State
            const updatedEpisode = {
                ...this.episodes[index],
                title: data.title,
                thumb: data.thumb,
                bgmUrl: data.bgm, // Standardize on 'bgmUrl' for state, or 'bgm'
                links: finalLinks,
                status: data.status,
                updatedAt: new Date().toISOString(),
                id: epId, // Ensure ID is in state
                displayMode: data.displayMode || 'webtoon' // [FIX] Persist Display Mode in Local State
            };
            this.episodes[index] = updatedEpisode;

            // [FIX] Enforce Approval Workflow (Server-Side Gate)
            let statusToSave = data.status || 'draft';
            let isApprovalRequest = false;

            if (statusToSave === 'published') {
                const user = window.user || {};
                const isAdmin = user.role === 'admin' || user.isSuperAdmin;

                // 1. Optimistic Check (Admin always passes)
                let isSeriesLive = isAdmin;

                // 2. Server Check (If not admin, verify Series Status)
                if (!isSeriesLive) {
                    try {
                        const { getDocFromServer } = await import("firebase/firestore");
                        // Fetch FRESH status from Server (Bypass Cache)
                        // This prevents "I have old cache saying it's draft" bugs
                        const snap = await getDocFromServer(doc(db, "series", this.seriesId));
                        const serverData = snap.data();

                        // [GATE LOGIC: "Lifetime Pass"]
                        // 1. Currently Published? -> PASS
                        // 2. Has History (publishedAt exists)? -> PASS (Re-publish Freedom)
                        if (serverData && (serverData.status === 'published' || serverData.publishedAt)) {
                            isSeriesLive = true;
                        }
                    } catch (e) {
                        console.warn("[Gate] Server check failed, fallback to local:", e);
                        // Fallback: trust local status if server check fails (Unlikely)
                        // Or fail safe? Let's fail safe to pending.
                    }
                }

                // 3. Final Decision
                if (!isSeriesLive) {
                    statusToSave = 'pending';
                    isApprovalRequest = true;
                }
            }

            // 4. DB Update (Batch: Array + Subcollection)
            const batch = writeBatch(db);
            const seriesRef = doc(db, "series", this.seriesId);

            // A. Update Parent Array (Legacy/Meta)
            batch.update(seriesRef, {
                // episodes: this.episodes, // [MIGRATION] REMOVED (Legacy Array Stop)
                updatedAt: serverTimestamp()
            });

            // B. Sync to Subcollection (Primary Source of Truth)
            // epRef is already defined above

            const epData = {
                seriesId: this.seriesId,
                title: updatedEpisode.title || `Episode ${index + 1}`,
                imageUrls: finalLinks || [],
                thumbnail: updatedEpisode.thumb || '',
                bgmUrl: updatedEpisode.bgmUrl || '',
                status: statusToSave,
                index: index + 1,
                order: index + 1, // [FIX] Add 'order' field for SeriesEditorModal query
                updatedAt: serverTimestamp(),
                id: epId,
                displayMode: updatedEpisode.displayMode || 'webtoon' // [FIX] Persist Display Mode to Subcollection
            };

            const user = window.user || {};
            const authorId = this.data.authorId || user.uid;
            epData.authorId = authorId;

            // [FIX] Proactive Guard: Clean undefined values to prevent Firestore Error
            Object.keys(epData).forEach(key => epData[key] === undefined && delete epData[key]);

            batch.set(epRef, epData, { merge: true });

            await batch.commit();

            if (!silent) {
                if (isApprovalRequest) {
                    if (window.Modal) window.Modal.toast(window.locale.t('editor.approval_request_sent'));
                } else if (statusToSave === 'published') {
                    if (window.Modal) window.Modal.toast(window.locale.t('editor.publish_success'));
                } else {
                    if (window.Modal) window.Modal.toast(window.locale.t('editor.temp_saved_toast'));
                }
            } else {
                console.log("[Controller] Auto-Saved & Synced");
            }

        } catch (e) {
            console.error("Save Error:", e);
            if (!silent) window.Modal.alert("Save Failed: " + e.message);
        }
    }

    async deleteEpisode(index) {
        const ep = this.episodes[index];
        if (!ep) return;

        try {
            // 1. Delete from Subcollection (Primary)
            // Even if it's legacy, trying to delete by ID from subcollection is safe.
            if (ep.id) {
                const epRef = doc(db, "series", this.seriesId, "episodes", ep.id);
                // We use deleteDoc imported below (Wait, need to add import)
                await deleteDoc(epRef);
            }

            // 2. Update Local State
            this.episodes.splice(index, 1);

            // 3. Sync Legacy Array - REMOVED (Single Source of Truth)
            // [MIGRATION] We no longer update the parent array to avoid 1MB limit.
            // The subcollection is now the sole source of truth.
            console.log("[WebtoonEditor] Skipped Legacy Parent Array Update (1MB Limit Prot)");

            if (window.Modal) window.Modal.toast(window.locale.t('editor.deleted_toast'));

        } catch (e) {
            console.error("Delete failed", e);
            throw e;
        }
    }
}
